import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

interface FileUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  accept?: string;
  onFileSelect: (file: File, onReset: () => void) => Promise<void>;
  onDownloadTemplate?: () => void;
  showDownloadTemplate?: boolean;
  isUploading?: boolean;
}

export function FileUploadDialog({
  open,
  onOpenChange,
  title,
  description,
  accept = ".csv",
  onFileSelect,
  onDownloadTemplate,
  showDownloadTemplate = true,
  isUploading = false,
}: FileUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(accept)) {
      setSelectedFile(file);
    } else {
      toast.error(`Please upload a ${accept} file`);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = () => {
    if (onDownloadTemplate && showDownloadTemplate) {
      onDownloadTemplate();
    }
  };

  const handleImport = async () => {
    if (selectedFile) {
      await onFileSelect(selectedFile, handleReset);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div
          className={`relative flex min-h-[250px] sm:min-h-[300px] md:min-h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-3 sm:p-4 md:p-6 transition-colors ${
            isDragging ? "border-primary bg-primary/5" : "border-border"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center gap-3 sm:gap-4 text-center w-full">
            <div className="rounded-full bg-primary/10 p-3 sm:p-4">
              <Upload className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-primary" />
            </div>
            <div className="space-y-1 sm:space-y-2">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold">
                Drop your {accept} file onto this area to upload
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                or click the button below
              </p>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={accept}
              className="sr-only"
            />
            <div className="flex flex-col gap-2 w-full max-w-xs">
              <Button
                variant="outline"
                className="w-full text-xs sm:text-sm"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose a {accept} file
              </Button>

              {showDownloadTemplate && (
                <Button
                  variant="link"
                  className="w-full text-xs sm:text-sm"
                  size="sm"
                  onClick={handleDownloadTemplate}
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    Download Sample Template
                  </span>
                  <span className="sm:hidden">Download Template</span>
                </Button>
              )}
            </div>
            {selectedFile && (
              <div className="flex items-center gap-2 mt-2 w-full justify-between max-w-xs">
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  Selected: {selectedFile.name}
                </p>
                <Button
                  variant="ghost"
                  onClick={handleRemoveFile}
                  className=" text-red-500 hover:text-red-600 shrink-0"
                  size="icon"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onOpenChange(false);
              setSelectedFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!selectedFile || isUploading}
            size="sm"
            onClick={handleImport}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Importing...
              </>
            ) : (
              "Import"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
